import { ActionRepository } from './ActionRepository';
import { ISessionService } from '../session';
import { AuthContextEnum, IAuthTokenService } from '../authTokenService';
import { IAuthProviderService } from '../authProviderService';
import { IActionService } from './types';
import { validateActions } from '../../clients/shell/actions';
import { SetServiceDependencies } from '../../infra/commonInitializer/types';

export class ActionService implements IActionService {
  private _actionRepository: ActionRepository;
  private _sessionService: ISessionService;
  private _authTokenService: IAuthTokenService;
  private _authProviderService: IAuthProviderService;

  public setDependencies({
    services,
    repositories
  }: SetServiceDependencies): void {
    this._actionRepository = repositories.actionRepository;
    this._sessionService = services.sessionService;
    this._authTokenService = services.authTokenService;
    this._authProviderService = services.authProviderService;
  }

  /**
   * Checks if the actions are allowed for the given auth context.
   * If an action is not present in the ActionRepository, addNew is called to fetch its allowed status.
   * @returns Returns true if all actions are allowed, false otherwise.
   */
  public async check(
    authContext: AuthContextEnum,
    actionsToCheck: string[]
  ): Promise<boolean> {
    if (!this._sessionService.isLoggedIn()) return false;

    // Add new actions to the repository if they are not already cached.
    await this.addIfNew(authContext, actionsToCheck);

    const repositoryKey = this._authTokenService.getSuffix(authContext);
    const cachedActionsMap =
      this._actionRepository.findOne(repositoryKey) || {};

    return actionsToCheck.every(
      (actionToCheck) => cachedActionsMap[actionToCheck]
    );
  }

  /**
   * Adds new actions to the cached map of actions, fetching their allowed status.
   * Ignores actions that are already cached.
   * @returns Returns true if new actions were added, false otherwise.
   */
  public async addIfNew(
    authContext: AuthContextEnum,
    actionsToAdd: string[]
  ): Promise<boolean> {
    if (!this._sessionService.isLoggedIn()) return false;
    if (actionsToAdd.length === 0) return false;

    const repositoryKey = this._authTokenService.getSuffix(authContext);
    const cachedActionsMap =
      this._actionRepository.findOne(repositoryKey) || {};

    const uncachedActions = actionsToAdd.filter(
      (actionToAdd) => !Object.keys(cachedActionsMap).includes(actionToAdd)
    );

    if (uncachedActions.length === 0) {
      return false;
    }

    // Calls /validate-actions endpoint.
    const allowedActionValues = await validateActions(
      this._authProviderService.createAuthProviderByAuthContextEnum(
        authContext
      ),
      uncachedActions
    );

    uncachedActions.forEach((action) => {
      cachedActionsMap[action] = allowedActionValues.includes(action);
    });

    this._actionRepository.save(repositoryKey, cachedActionsMap);

    return true;
  }

  /**
   * Revalidates all actions that are currently cached in the ActionRepository.
   * @returns Returns true if any action's status has changed, false otherwise.
   */
  public async refresh(authContext: AuthContextEnum): Promise<boolean> {
    if (!this._sessionService.isLoggedIn()) return false;

    const repositoryKey = this._authTokenService.getSuffix(authContext);
    const cachedActionsMap = this._actionRepository.findOne(repositoryKey);
    if (!cachedActionsMap || Object.keys(cachedActionsMap).length === 0) {
      return false;
    }

    const previousAllowedActions = Object.keys(cachedActionsMap).filter(
      (action) => cachedActionsMap[action]
    );

    const newAllowedActions = await validateActions(
      this._authProviderService.createAuthProviderByAuthContextEnum(
        authContext
      ),
      Object.keys(cachedActionsMap)
    );

    // Check if the allowed actions have not changed.
    if (
      previousAllowedActions.length === newAllowedActions.length &&
      previousAllowedActions.every((prevValue) =>
        newAllowedActions.includes(prevValue)
      )
    ) {
      return false;
    }

    Object.keys(cachedActionsMap).forEach((cachedAction) => {
      cachedActionsMap[cachedAction] = newAllowedActions.includes(cachedAction);
    });

    this._actionRepository.save(repositoryKey, cachedActionsMap);

    return true;
  }

  public normalizeActionNames(policies: string[], prefix?: string): string[] {
    return policies.map((policy) => {
      if (prefix && !policy.startsWith(prefix)) {
        return `${prefix}${policy}`;
      }

      return policy;
    });
  }
}
